import { Assets, Container, Sprite, Texture } from 'pixi.js';
import { computeSceneLayout, type SceneLayout, type SceneLayoutConfig } from './layout';

export interface MainScene {
  container: Container;
  player: Sprite;
  enemies: Sprite[];
  layout: SceneLayout;
}

export type SceneTextures = Record<string, Texture>;

export const SCENE_TEXTURE_KEYS = [
  'player',
  'invader_squid_a',
  'invader_crab_a',
  'invader_octopus_a',
] as const;

export async function loadSceneTextures(
  baseUrl = './sprites',
): Promise<SceneTextures> {
  const entries = await Promise.all(
    SCENE_TEXTURE_KEYS.map(async (key) => {
      const texture = await Assets.load<Texture>(`${baseUrl}/${key}.png`);
      texture.source.scaleMode = 'nearest';
      return [key, texture] as const;
    }),
  );
  return Object.fromEntries(entries);
}

export function createMainScene(
  parent: Container,
  textures: SceneTextures,
  config: SceneLayoutConfig,
): MainScene {
  const layout = computeSceneLayout(config);
  const container = new Container();
  container.label = 'main-scene';
  parent.addChild(container);

  const playerTexture = textures[layout.player.textureKey];
  if (!playerTexture) {
    throw new Error(`Missing texture: ${layout.player.textureKey}`);
  }
  const player = new Sprite(playerTexture);
  player.label = 'player';
  player.x = layout.player.x;
  player.y = layout.player.y;
  player.scale.set(layout.player.scale);
  player.tint = layout.player.tint;
  container.addChild(player);

  const enemies: Sprite[] = layout.enemies.map((item) => {
    const tex = textures[item.textureKey];
    if (!tex) throw new Error(`Missing texture: ${item.textureKey}`);
    const sprite = new Sprite(tex);
    sprite.label = `enemy-${item.row}-${item.col}`;
    sprite.x = item.x;
    sprite.y = item.y;
    sprite.scale.set(item.scale);
    sprite.tint = item.tint;
    container.addChild(sprite);
    return sprite;
  });

  return { container, player, enemies, layout };
}
